/**
 *	The renderer's Markdoc baseline.
 *
 *	Merged *under* whatever an app hands <DocumentsProvider>, so registering a
 *	tag of your own never costs you what every documentation page is entitled to
 *	— today, GitHub's alert blockquotes, the `%` ingress and coloured code
 *	fences.
 */
import {
	type Config,
	type Node,
	type RenderableTreeNode,
	Tag,
	nodes,
} from '@markdoc/markdoc';

import { Alert, type AlertType, alertTypes } from '@shared/docs/alert';
import { Anchor } from '@shared/docs/anchor';
import { Ingress } from '@shared/docs/ingress';
import { Norrsken } from '@shared/docs/norrsken';
import type { DocumentComponents } from '@shared/docs/provider';
import Link from '@shared/router/link';

/** The tag name `Alert` is registered under, see `baseComponents`. */
const ALERT_TAG = 'Alert';

/** The tag name `Ingress` is registered under, see `baseComponents`. */
const INGRESS_TAG = 'Ingress';

/** The tag name `Norrsken` is registered under, see `baseComponents`. */
const NORRSKEN_TAG = 'Norrsken';

/** The tag name `Link` is registered under, see `baseComponents`. */
const LINK_TAG = 'Link';

/** The tag name `Anchor` is registered under, see `baseComponents`. */
const ANCHOR_TAG = 'Anchor';

/** The href prefix that routes a link through the router rather than the browser. */
const ROUTED_PREFIX = '/';

/** The href prefix that scrolls inside the page rather than leaving it. */
const FRAGMENT_PREFIX = '#';

/**
 *	An href naming its own scheme, e.g. `https://example.com`. Deliberately not
 *	`mailto:` or `tel:`, which have no `//` and are handed to the platform.
 */
const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\//i;

/** Headings that carry a generated id — `h1` is the page title, see `withHeadingIds`. */
const ANCHORED_HEADINGS = new Set(['h2', 'h3', 'h4', 'h5', 'h6']);

/** Opens an ingress, the way `>` opens a blockquote. */
const INGRESS_MARKER = '% ';

const markers: Map<string, AlertType> = new Map(
	alertTypes.map((type) => [type.toUpperCase(), type])
);

/** `[!NOTE]`, and whatever the author wrote after it as the alert's own title. */
const MARKER = /^\[!([A-Z]+)\](.*)$/;

interface Alerted {
	type: AlertType;
	title: Optional<string>;
	children: RenderableTreeNode[];
}

/**
 *	Reads an alert out of an already-transformed blockquote, or nothing when it
 *	is an ordinary quote.
 *
 *	Matching the marker at the head of a child is what enforces GitHub's "first
 *	line" rule: markdown-it ends that text node at the line break, so whatever
 *	follows the marker in it is the rest of that line and nothing beyond it —
 *	the alert's title, standing in for the type's own label.
 */
function toAlert(children: RenderableTreeNode[]): Optional<Alerted> {
	const [paragraph, ...siblings] = children;

	if (!Tag.isTag(paragraph) || paragraph.name !== 'p') {
		return undefined;
	}

	const [marker, ...body] = paragraph.children;

	if (typeof marker !== 'string') {
		return undefined;
	}

	const match = MARKER.exec(marker);

	if (!match) {
		return undefined;
	}

	const [, name = '', title = ''] = match;

	const type = markers.get(name);

	if (!type) {
		return undefined;
	}

	// The softbreak closing the marker's line transforms to a plain space;
	// keeping it would indent the alert's first word.
	const content = body[0] === ' ' ? body.slice(1) : body;

	return {
		type,
		title: title.trim() || undefined,
		children: content.length
			? [new Tag(paragraph.name, paragraph.attributes, content), ...siblings]
			: siblings,
	};
}

/**
 *	An alert is an ordinary blockquote as far as the grammar is concerned, so it
 *	can only be recognised after the fact: transform the quote as Markdoc would,
 *	then look for the marker in what came out.
 */
function transformBlockquote(node: Node, config: Config): RenderableTreeNode {
	const children = node.transformChildren(config);
	const alert = toAlert(children);

	if (!alert) {
		return new Tag('blockquote', {}, children);
	}

	return new Tag(
		ALERT_TAG,
		{ type: alert.type, title: alert.title },
		alert.children
	);
}

/**
 *	Reads an ingress out of an already-transformed paragraph, or nothing when it
 *	is an ordinary one.
 *
 *	The marker is stripped per line rather than once: markdown has no grammar
 *	for `%`, so the whole thing arrives as one paragraph whose continuation
 *	lines still carry theirs. A line break transforms to a plain space, which is
 *	what tells a continuation's marker from a stray `%` mid-sentence.
 */
function toIngress(
	children: RenderableTreeNode[]
): Optional<RenderableTreeNode[]> {
	const [first] = children;

	if (typeof first !== 'string' || !first.startsWith(INGRESS_MARKER)) {
		return undefined;
	}

	return children.map((child, index) => {
		if (typeof child !== 'string') {
			return child;
		}

		if (index === 0) {
			return child.slice(INGRESS_MARKER.length);
		}

		const continuation =
			children[index - 1] === ' ' && child.startsWith(INGRESS_MARKER);

		return continuation ? child.slice(INGRESS_MARKER.length) : child;
	});
}

/**
 *	An ingress is an ordinary paragraph as far as the grammar is concerned, so
 *	it can only be recognised after the fact — the same shape as the blockquote
 *	override above.
 */
function transformParagraph(node: Node, config: Config): RenderableTreeNode {
	const children = node.transformChildren(config);
	const ingress = toIngress(children);

	if (!ingress) {
		return new Tag('p', {}, children);
	}

	return new Tag(INGRESS_TAG, {}, ingress);
}

/**
 *	Hands the fence's source and info string to the highlighter rather than
 *	transforming its children — the block's text is one string to tokenize, and
 *	splitting it into a tree first would only have to be put back together.
 */
function transformFence(node: Node): RenderableTreeNode {
	const { content, language } = node.attributes;

	return new Tag(NORRSKEN_TAG, { content, language }, []);
}

/**
 *	Sends a link wherever its href says it goes: in-page, in-router, or out of
 *	the app entirely.
 *
 *	The three tests are ordered, and `//example.com` is why. It names no scheme
 *	but is not a path either — it falls to <Link>, which already recognises it
 *	as foreign and opens it beside the app. Everything without a leading `/`,
 *	`#` or scheme (`mailto:`, `tel:`) stays a plain anchor for the platform to
 *	handle.
 */
function transformLink(node: Node, config: Config): RenderableTreeNode {
	const attributes = node.transformAttributes(config);
	const children = node.transformChildren(config);

	const { href } = attributes;

	if (typeof href !== 'string') {
		return new Tag('a', attributes, children);
	}

	if (href.startsWith(FRAGMENT_PREFIX)) {
		return new Tag(ANCHOR_TAG, attributes, children);
	}

	// Anything carrying a scheme leaves the documentation, so it opens beside
	// it rather than replacing it — the app's own host included, since an
	// author who wrote the domain out meant the address, not the route.
	if (ABSOLUTE_URL.test(href)) {
		const external = {
			...attributes,
			target: '_blank',
			rel: 'noreferrer noopener',
		};

		return new Tag('a', external, children);
	}

	return new Tag(
		href.startsWith(ROUTED_PREFIX) ? LINK_TAG : 'a',
		attributes,
		children
	);
}

/** The text a heading reads as, with its emphasis and code spans flattened. */
function toText(node: RenderableTreeNode): string {
	if (typeof node === 'string') {
		return node;
	}

	if (typeof node === 'number') {
		return String(node);
	}

	if (Array.isArray(node)) {
		return node.map(toText).join('');
	}

	if (Tag.isTag(node)) {
		return node.children.map(toText).join('');
	}

	return '';
}

/** `## Adding a token` -> `adding-a-token`. */
export function toHeadingId(text: string): string {
	return text
		.toLowerCase()
		.replace(/[^\p{L}\p{N} -]/gu, '')
		.trim()
		.replace(/[\s-]+/g, '-');
}

/**
 *	Gives every `h2`–`h6` an id derived from its own text, so `[jump](#section)`
 *	has something to scroll to.
 *
 *	A whole-document pass rather than a node transform, for two reasons. Ids
 *	have to be unique across the page, and a node transform sees one node with
 *	no knowledge of its siblings. And an app may override the `heading` node —
 *	the playbook does, to hang the Figma actions off the title — which would
 *	replace a transform here but never this.
 *
 *	`h1` is left alone: a page has exactly one, it is the title `parseTitle`
 *	reads, and the page's own URL already addresses it.
 */
export function withHeadingIds(
	content: RenderableTreeNode
): RenderableTreeNode {
	const taken: Map<string, number> = new Map();

	function visit(node: RenderableTreeNode): RenderableTreeNode {
		if (!Tag.isTag(node)) {
			return node;
		}

		const children = node.children.map(visit);

		if (!ANCHORED_HEADINGS.has(node.name) || node.attributes['id']) {
			return new Tag(node.name, node.attributes, children);
		}

		const slug = toHeadingId(toText(node));

		// A heading of nothing but punctuation slugs to an empty string, which
		// is not an id anything can address.
		if (!slug) {
			return new Tag(node.name, node.attributes, children);
		}

		// Two headings reading the same is a page an author still has to be
		// able to link into, so the second becomes `<slug>-1`, as GitHub's do.
		const seen = taken.get(slug) ?? 0;

		taken.set(slug, seen + 1);

		const id = seen === 0 ? slug : `${slug}-${seen}`;

		return new Tag(node.name, { ...node.attributes, id }, children);
	}

	return visit(content);
}

export const baseConfig: Config = {
	nodes: {
		blockquote: { ...nodes.blockquote, transform: transformBlockquote },
		paragraph: { ...nodes.paragraph, transform: transformParagraph },
		fence: { ...nodes.fence, transform: transformFence },
		link: { ...nodes.link, transform: transformLink },
	},
	tags: {},
	variables: {},
};

export const baseComponents: DocumentComponents = {
	[ALERT_TAG]: Alert,
	[ANCHOR_TAG]: Anchor,
	[INGRESS_TAG]: Ingress,
	[NORRSKEN_TAG]: Norrsken,
	[LINK_TAG]: Link,
};

/** An app's Markdoc configuration, laid over the baseline above. */
export function withBaseConfig(config: Config): Config {
	return {
		...config,
		nodes: { ...baseConfig.nodes, ...config.nodes },
		tags: { ...baseConfig.tags, ...config.tags },
	};
}

/** An app's tag components, laid over the baseline above. */
export function withBaseComponents(
	components: DocumentComponents
): DocumentComponents {
	return { ...baseComponents, ...components };
}
